// src/pages/Admin/SiparislerPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Search, Filter, ChevronLeft, ChevronRight, Loader2, Eye, Trash2, Pencil, Check, X, Plus,
  Package2, Truck, CreditCard, ClipboardList, Mail, User2, Hash, MapPin
} from "lucide-react";
import Modal from "../../../components/Common/Modal/Modal";
import { adminOrdersApi, ASiparisCardDto, ASiparisDetayResponse } from "../../../api/adminOrders";

const trCurrency = (v: number, ccy = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: ccy }).format(v || 0);
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleString("tr-TR") : "—");

// ---- Durum & Ödeme rozetleri ----
const statusMap: Record<number, { text: string; cls: string }> = {
  0: { text: "Alındı", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  4: { text: "Teslim Edilemedi", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  6: { text: "Kargoda", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  7: { text: "Teslim", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  8: { text: "İptal", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  9: { text: "İade", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  18:{ text: "Teslim Edilemedi", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};
const payMap: Record<number, { text: string; cls: string }> = {
  0: { text: "Ödenmedi", cls: "bg-gray-50 text-gray-700 border-gray-200" },
  1: { text: "Ödendi", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  2: { text: "Kısmi", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  3: { text: "İade", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};
const Badge: React.FC<{ kind: "status" | "pay"; code: number }> = ({ kind, code }) => {
  const m = (kind === "status" ? statusMap : payMap)[code] || {
    text: `Kod ${code}`,
    cls: "bg-gray-50 text-gray-700 border-gray-200",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${m.cls}`}>{m.text}</span>;
};

// ---- Adres yardımcıları ----
type RawAddr = Record<string, any> | null;
const parseAddress = (json?: string | null): RawAddr => { try { return json ? JSON.parse(json) : null; } catch { return null; } };
const get = (o: RawAddr, ...keys: string[]) => { if (!o) return undefined; for (const k of keys) { if (o[k] != null) return o[k]; } };
const AddressBox: React.FC<{ title: string; json?: string | null }> = ({ title, json }) => {
  const a = parseAddress(json);
  if (!a) return null;
  const adSoyad = get(a, "adSoyad", "AdSoyad");
  const tel = get(a, "telefon", "Telefon");
  const ulke = get(a, "ulke", "Ulke");
  const sehir = get(a, "sehir", "Sehir");
  const ilce = get(a, "ilce", "Ilce");
  const pk = get(a, "postaKodu", "PostaKodu");
  const s1 = get(a, "adresSatiri1", "AdresSatiri1");
  const s2 = get(a, "adresSatiri2", "AdresSatiri2");
  const vd = get(a, "vergiDairesi", "VergiDairesi");
  const vn = get(a, "vergiNo", "VergiNo");

  return (
    <div className="border rounded-lg p-3 bg-white shadow-sm">
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1"><MapPin size={14} />{title}</div>
      <div className="text-sm">
        {adSoyad && <div className="font-medium">{adSoyad}</div>}
        <div className="text-gray-700">{[s1, s2].filter(Boolean).join(" ") || "—"}</div>
        <div className="text-gray-700">{[ilce, sehir, pk].filter(Boolean).join(" / ") || "—"}</div>
        {ulke && <div className="text-gray-700">{ulke}</div>}
        {tel && <div className="text-gray-600 mt-1">Tel: {String(tel).trim()}</div>}
        {(vd || vn) && <div className="text-gray-600 mt-1">VD: {vd || "—"} • VN: {vn || "—"}</div>}
      </div>
    </div>
  );
};

// ============================ Sayfa ============================
const SiparislerPage: React.FC = () => {
  // filtre state
  const [q, setQ] = useState("");
  const [durumKodu, setDurumKodu] = useState<number | undefined>(undefined);
  const [odemeDurumu, setOdemeDurumu] = useState<number | undefined>(undefined);
  const [musteriId, setMusteriId] = useState<number | undefined>(undefined);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [sort, setSort] = useState<"yeni" | "eski">("yeni");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // liste state
  const [rows, setRows] = useState<ASiparisCardDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string>();

  // detay modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | undefined>(undefined);

  const fetchList = async () => {
    setLoading(true); setErr(undefined);
    try {
      const res = await adminOrdersApi.list({
        q,
        durumKodu,
        odemeDurumu,
        musteriId,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        pageSize,
        sort,
      });
      if (!res.success || !res.data) throw new Error(res.errorDetail || "Liste alınamadı");
      setRows(res.data.items);
      setTotal(res.data.total);
    } catch (e: any) {
      setErr(e?.message || "Hata");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [page, pageSize, sort]);

  const applyFilters = () => { setPage(1); fetchList(); };
  const clearFilters = () => {
    setQ(""); setDurumKodu(undefined); setOdemeDurumu(undefined); setMusteriId(undefined);
    setDateFrom(""); setDateTo(""); setSort("yeni"); setPage(1);
    fetchList();
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const openDetail = (id: number) => { setDetailId(id); setDetailOpen(true); };
  const closeDetail = () => { setDetailOpen(false); };

  const onDelete = async (id: number) => {
    if (!window.confirm("Bu siparişi silmek (soft) istediğinize emin misiniz?")) return;
    const r = await adminOrdersApi.deleteOrder(id);
    if (!r.success) alert(r.errorDetail || "Silinemedi");
    await fetchList();
  };

  // =================== UI ===================
  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Başlık */}
      <div className="bg-white border rounded-lg shadow-sm p-3 sm:p-3.5 md:p-4 flex items-center gap-2">
        <ClipboardList className="text-gray-700" />
        <h1 className="text-lg sm:text-xl md:text-2xl font-normal ">Siparişler</h1>
      </div>

      {/* Filtreler – mobil minimal padding */}
      <div className="bg-white border rounded-lg shadow-sm p-3 md:p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 md:gap-3">
          <div className="md:col-span-2 flex items-center gap-1.5 border rounded px-2 h-10">
            <Search size={16} className="text-gray-500" />
            <input
              className="flex-1 outline-none text-sm"
              placeholder="Sipariş No / Müşteri ID"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <select
            className="h-10 border rounded px-2 text-sm"
            value={durumKodu ?? ""}
            onChange={(e) => setDurumKodu(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">Durum (hepsi)</option>
            <option value="0">Alındı</option>
            <option value="6">Kargoda</option>
            <option value="7">Teslim</option>
            <option value="8">İptal</option>
            <option value="9">İade</option>
            <option value="4">Teslim Edilemedi</option>
            <option value="18">Teslim Edilemedi (18)</option>
          </select>
          <select
            className="h-10 border rounded px-2 text-sm"
            value={odemeDurumu ?? ""}
            onChange={(e) => setOdemeDurumu(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">Ödeme (hepsi)</option>
            <option value="0">Ödenmedi</option>
            <option value="1">Ödendi</option>
            <option value="2">Kısmi</option>
            <option value="3">İade</option>
          </select>
          <input
            className="h-10 border rounded px-2 text-sm"
            type="number"
            placeholder="Müşteri ID"
            value={musteriId ?? ""}
            onChange={(e) => setMusteriId(e.target.value ? Number(e.target.value) : undefined)}
          />
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-500" />
            <select className="h-10 border rounded px-2 flex-1 text-sm" value={sort} onChange={(e) => setSort(e.target.value as any)}>
              <option value="yeni">Yeni → Eski</option>
              <option value="eski">Eski → Yeni</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 md:gap-3">
          <div className="flex items-center gap-2 md:col-span-2">
            <input className="h-10 border rounded px-2 w-full text-sm" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <span className="text-gray-500">→</span>
            <input className="h-10 border rounded px-2 w-full text-sm" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm text-gray-600">Sayfa boyutu</span>
            <select
              className="h-10 border rounded px-2 text-sm"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {[10, 25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="md:col-span-3 flex gap-2 justify-end">
            <button className="h-10 px-3 border rounded hover:bg-gray-50 text-sm" onClick={clearFilters}>Temizle</button>
            <button className="h-10 px-3 rounded text-white text-sm" style={{ background: "#16a34a" }} onClick={applyFilters}>Uygula</button>
          </div>
        </div>
      </div>

      {/* Hata */}
      {!!err && <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded">{err}</div>}

      {/* --------- Mobil Kart Listesi (sm) --------- */}
      <div className="space-y-2 md:hidden">
        {loading ? (
          <div className="p-6 text-center text-gray-500"><Loader2 className="inline animate-spin" /> Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-gray-500">Kayıt bulunamadı</div>
        ) : rows.map(r => (
          <div key={r.siparisId} className="bg-white border rounded-lg shadow-sm p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">{r.siparisNo}</div>
                <div className="text-xs text-gray-500">#{r.siparisId}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold">{trCurrency(r.genelToplamBrut, r.paraBirimiKodu)}</div>
                <div className="text-xs text-gray-600">{fmtDate(r.siparisTarihi)}</div>
              </div>
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <div className="border rounded px-2 py-1 bg-gray-50">
                <div className="text-gray-600 text-xs mb-0.5">Durum</div>
                <Badge kind="status" code={r.durumKodu} />
              </div>
              <div className="border rounded px-2 py-1 bg-gray-50">
                <div className="text-gray-600 text-xs mb-0.5">Ödeme</div>
                <Badge kind="pay" code={r.odemeDurumu} />
              </div>
            </div>

            <div className="mt-2 border rounded-lg p-2 bg-gray-50">
              <div className="flex items-center gap-1 text-xs text-gray-600 mb-1"><User2 size={14} />Müşteri</div>
              <div className="text-sm font-medium">
                {r.musteriAdSoyad?.trim() || (r.musteriId ? `#${r.musteriId}` : "Misafir")}
              </div>
              {r.musteriEposta && (
                <div className="text-xs text-gray-600 flex items-center gap-1"><Mail size={12} />{r.musteriEposta}</div>
              )}
            </div>

            <div className="mt-2 flex gap-2">
              <button className="flex-1 h-9 px-3 border rounded" onClick={() => openDetail(r.siparisId)}>
                <Eye size={16} className="inline mr-1" /> Detay
              </button>
              <button className="h-9 px-3 border rounded text-rose-600" onClick={() => onDelete(r.siparisId)}>
                <Trash2 size={16} className="inline mr-1" /> Sil
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* --------- Masaüstü Tablo (md+) --------- */}
      <div className="bg-white border rounded-lg shadow-sm overflow-x-auto hidden md:block">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-xs text-gray-600">
              {["No", "Müşteri", "Durum", "Ödeme", "Tutar", "Tarih", "İşlemler"].map(h => (
                <th key={h} className="px-3 py-2 text-left uppercase">{h}</th>
              ))}
            </tr>
          </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7} className="p-6 text-center text-gray-500"><Loader2 className="inline animate-spin" /> Yükleniyor…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={7} className="p-6 text-center text-gray-500">Kayıt bulunamadı</td></tr>
          ) : rows.map(r => (
            <tr key={r.siparisId} className="border-t hover:bg-gray-50">
              <td className="px-3 py-2">
                <div className="font-medium">{r.siparisNo}</div>
                <div className="text-xs text-gray-500">#{r.siparisId}</div>
              </td>
              <td className="px-3 py-2">
                <div className="font-medium">{r.musteriAdSoyad?.trim() || (r.musteriId ? `#${r.musteriId}` : "Misafir")}</div>
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  {r.musteriEposta && (<><Mail size={12} />{r.musteriEposta}</>)}
                  <span className="ml-2 inline-flex items-center gap-1 text-gray-400"><Hash size={12} />{r.musteriId ?? "-"}</span>
                </div>
              </td>
              <td className="px-3 py-2"><Badge kind="status" code={r.durumKodu} /></td>
              <td className="px-3 py-2"><Badge kind="pay" code={r.odemeDurumu} /></td>
              <td className="px-3 py-2 font-semibold">{trCurrency(r.genelToplamBrut, r.paraBirimiKodu)}</td>
              <td className="px-3 py-2 text-sm text-gray-700">{fmtDate(r.siparisTarihi)}</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <button className="px-2 py-1 border rounded hover:bg-white" title="Detay" onClick={() => openDetail(r.siparisId)}><Eye size={16} /></button>
                  <button className="px-2 py-1 border rounded hover:bg-white text-rose-600" title="Sil (soft)" onClick={() => onDelete(r.siparisId)}><Trash2 size={16} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        </table>

        {/* Sayfalama */}
        <div className="flex items-center justify-between p-3 border-t text-sm">
          <div className="text-gray-600">
            Toplam <b>{total}</b> kayıt, sayfa <b>{page}</b>/<b>{totalPages}</b>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="h-9 w-9 grid place-items-center border rounded disabled:opacity-50"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
            ><ChevronLeft size={16} /></button>
            <button
              className="h-9 w-9 grid place-items-center border rounded disabled:opacity-50"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            ><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      {/* Detay modal */}
      <OrderDetailModal
        open={detailOpen}
        onClose={closeDetail}
        siparisId={detailId}
        onUpdated={fetchList}
      />
    </div>
  );
};

export default SiparislerPage;

// ================= Detay Modal =================
const OrderDetailModal: React.FC<{
  open: boolean;
  siparisId?: number;
  onClose: () => void;
  onUpdated: () => void;
}> = ({ open, siparisId, onClose, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<ASiparisDetayResponse | null>(null);
  const [error, setError] = useState<string>();
  const [editStatus, setEditStatus] = useState<number | undefined>();
  const [note, setNote] = useState<string>("");

  const load = async () => {
    if (!siparisId) return;
    setLoading(true); setError(undefined);
    try {
      const r = await adminOrdersApi.detail(siparisId);
      if (!r.success || !r.data) throw new Error(r.errorDetail || "Detay alınamadı");
      setRes(r.data);
      setEditStatus(r.data.header.durumKodu);
      setNote(r.data.header.notlar || "");
    } catch (e: any) {
      setError(e?.message || "Hata");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, siparisId]);

  const header = res?.header;
  const items = res?.items || [];
  const payments = res?.payments || [];
  const shipments = res?.shipments || [];
  const ccy = header?.paraBirimiKodu || "TRY";

  const doUpdateStatus = async () => {
    if (!header) return;
    const r = await adminOrdersApi.updateStatus({ siparisId: header.siparisId, durumKodu: editStatus ?? header.durumKodu });
    if (!r.success) return alert(r.errorDetail || "Güncellenemedi");
    await load(); onUpdated();
  };
  const doUpdateNote = async () => {
    if (!header) return;
    const r = await adminOrdersApi.updateNote({ siparisId: header.siparisId, notlar: note });
    if (!r.success) return alert(r.errorDetail || "Not kaydedilemedi");
    await load(); onUpdated();
  };

  // Ödeme ekle mini form
  const [pAmount, setPAmount] = useState<number>(0);
  const [pProvider, setPProvider] = useState<string>("");
  const [pRef, setPRef] = useState<string>("");
  const [pType, setPType] = useState<1 | 2 | 3>(1);
  const addPayment = async () => {
    if (!header) return;
    if (pAmount <= 0) return alert("Tutar > 0 olmalı");
    const r = await adminOrdersApi.addPayment({
      siparisId: header.siparisId,
      odemeSaglayici: pProvider || null,
      islemAnahtari: pRef || null,
      tutarBrut: pAmount,
      paraBirimiKodu: ccy,
      islemDurumu: pType,
    });
    if (!r.success) return alert(r.errorDetail || "Ödeme eklenemedi");
    setPAmount(0); setPProvider(""); setPRef(""); setPType(1);
    await load(); onUpdated();
  };

  // Sevkiyat ekle/güncelle
  const [shCompany, setShCompany] = useState("");
  const [shTrack, setShTrack] = useState("");
  const [shStatus, setShStatus] = useState(0);
  const [shNote, setShNote] = useState("");
  const addShipment = async () => {
    if (!header) return;
    const r = await adminOrdersApi.addShipment({
      siparisId: header.siparisId,
      kargoSirketAdi: shCompany || null,
      takipNo: shTrack || null,
      durumKodu: shStatus,
      notlar: shNote || null,
    });
    if (!r.success) return alert(r.errorDetail || "Sevkiyat eklenemedi");
    setShCompany(""); setShTrack(""); setShStatus(0); setShNote("");
    await load(); onUpdated();
  };
  const updateShipment = async (sevkiyatId: number, payload: Partial<{ kargoSirketAdi: string; takipNo: string; durumKodu: number; notlar: string; }>) => {
    if (!header) return;
    const r = await adminOrdersApi.updateShipment({
      sevkiyatId,
      siparisId: header.siparisId,
      kargoSirketAdi: payload.kargoSirketAdi ?? "",
      takipNo: payload.takipNo ?? "",
      durumKodu: payload.durumKodu ?? 0,
      notlar: payload.notlar ?? "",
    });
    if (!r.success) return alert(r.errorDetail || "Sevkiyat güncellenemedi");
    await load(); onUpdated();
  };
  const deleteShipment = async (sevkiyatId: number) => {
    if (!window.confirm("Sevkiyatı silmek istiyor musunuz?")) return;
    const r = await adminOrdersApi.deleteShipment(sevkiyatId);
    if (!r.success) return alert(r.errorDetail || "Sevkiyat silinemedi");
    await load(); onUpdated();
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={header ? `#${header.siparisId} • ${header.siparisNo}` : "Sipariş Detayı"}
      size="xxxxl"
      footer={<button className="px-3 sm:px-4 py-1.5 sm:py-2 border rounded hover:bg-gray-50 text-sm" onClick={onClose}>Kapat</button>}
    >
      {/* 95vw x 95vh, mobilde minimal padding */}
      <div className="w-[95vw] max-w-[95vw] h-[95vh] max-h-[95vh] p-2 md:p-3 overflow-auto">
        {loading ? (
          <div className="p-10 text-center text-gray-600"><Loader2 className="inline animate-spin" /> Yükleniyor…</div>
        ) : error ? (
          <div className="p-6 text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>
        ) : !header ? (
          <div className="p-6 text-gray-600">Kayıt bulunamadı.</div>
        ) : (
          <div className="space-y-4 md:space-y-6">
            {/* Üst özet */}
            <div className="grid md:grid-cols-3 gap-3 md:gap-4">
              {/* Müşteri */}
              <div className="bg-white border rounded-lg shadow-sm p-3">
                <div className="text-sm text-gray-600 mb-1">Müşteri</div>
                <div className="flex items-start gap-3">
                  <div className="shrink-0 rounded-full bg-gray-100 w-9 h-9 grid place-items-center">
                    <User2 size={18} className="text-gray-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {header.musteriAdSoyad?.trim() || (header.musteriId ? `#${header.musteriId}` : "Misafir")}
                    </div>
                    <div className="text-xs text-gray-600 flex items-center gap-1">
                      {header.musteriEposta && (<><Mail size={12} />{header.musteriEposta}</>)}
                      <span className="ml-2 inline-flex items-center gap-1 text-gray-400"><Hash size={12} />{header.musteriId ?? "-"}</span>
                    </div>
                    <div className="mt-2 text-xs text-gray-600">Tarih: <span className="font-medium">{fmtDate(header.siparisTarihi)}</span></div>
                  </div>
                </div>
              </div>

              {/* Durum & Ödeme */}
              <div className="bg-white border rounded-lg shadow-sm p-3">
                <div className="text-sm text-gray-600 mb-2">Durum</div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge kind="status" code={header.durumKodu} />
                  <select className="h-9 border rounded px-2 text-sm" value={editStatus} onChange={(e) => setEditStatus(Number(e.target.value))}>
                    <option value="0">Alındı</option>
                    <option value="6">Kargoda</option>
                    <option value="7">Teslim</option>
                    <option value="8">İptal</option>
                    <option value="9">İade</option>
                    <option value="4">Teslim Edilemedi</option>
                    <option value="18">Teslim Edilemedi (18)</option>
                  </select>
                  <button className="h-9 px-3 rounded text-white" style={{ background: "#2563eb" }} onClick={doUpdateStatus}><Check size={16} /></button>
                </div>
                <div className="mt-3 text-sm text-gray-600">Ödeme</div>
                <div className="mt-1"><Badge kind="pay" code={header.odemeDurumu} /></div>
              </div>

              {/* Toplamlar */}
              <div className="bg-white border rounded-lg shadow-sm p-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 rounded border bg-gray-50">
                    <div className="text-gray-600">Ara Toplam</div>
                    <div className="font-semibold">{trCurrency(header.araToplamNet, ccy)}</div>
                  </div>
                  <div className="p-2 rounded border bg-gray-50">
                    <div className="text-gray-600">İndirim</div>
                    <div className="font-semibold">{trCurrency(header.indirimToplamNet, ccy)}</div>
                  </div>
                  <div className="p-2 rounded border bg-gray-50">
                    <div className="text-gray-600">Kargo</div>
                    <div className="font-semibold">{trCurrency(header.kargoUcretiNet, ccy)}</div>
                  </div>
                  <div className="p-2 rounded border bg-gray-50">
                    <div className="text-gray-600">Genel Toplam</div>
                    <div className="font-semibold">{trCurrency(header.genelToplamBrut, ccy)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Adresler */}
            <div className="grid md:grid-cols-2 gap-3 md:gap-4">
              <AddressBox title="Teslimat Adresi" json={(header as any).teslimatAdresiJson ?? (header as any).TeslimatAdresiJson} />
              <AddressBox title="Fatura Adresi"   json={(header as any).faturaAdresiJson   ?? (header as any).FaturaAdresiJson} />
            </div>

            {/* Not */}
            <div className="bg-white border rounded-lg shadow-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold">İç Not</div>
                <button className="px-3 h-9 rounded text-white" style={{ background: "#16a34a" }} onClick={doUpdateNote}><Pencil size={16} /></button>
              </div>
              <textarea className="w-full border rounded p-2 min-h-[80px] text-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="İç notlar…" />
            </div>

            {/* Ürünler */}
            <div>
              <div className="flex items-center gap-2 mb-2"><Package2 size={16} /><div className="font-semibold">Ürünler</div></div>
              <div className="bg-white border rounded-lg shadow-sm">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-gray-600">
                      {["Ürün", "Varyant", "Adet", "Birim (Net)", "KDV", "Satır Toplam"].map(h => <th key={h} className="px-3 py-2 text-left">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(it => (
                      <tr key={it.siparisKalemId} className="border-t">
                        <td className="px-3 py-2">
                          <div className="font-medium">{it.urunAdi}</div>
                          <div className="text-xs text-gray-500">#{it.urunId}</div>
                        </td>
                        <td className="px-3 py-2">{it.secenekOzeti || "-"}</td>
                        <td className="px-3 py-2">{it.adet}</td>
                        <td className="px-3 py-2">{trCurrency(it.birimFiyatNet, ccy)}</td>
                        <td className="px-3 py-2">{it.vergiOraniYuzde}%</td>
                        <td className="px-3 py-2 font-semibold">{trCurrency(it.satirToplamBrut, ccy)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Ödemeler & Sevkiyatlar */}
            <div className="grid lg:grid-cols-2 gap-3 md:gap-4">
              {/* Ödemeler */}
              <div className="bg-white border rounded-lg shadow-sm p-3">
                <div className="flex items-center gap-2 mb-2"><CreditCard size={16} /><div className="font-semibold">Ödemeler</div></div>
                {payments.length === 0 ? (
                  <div className="text-sm text-gray-500">Kayıt yok.</div>
                ) : (
                  <div className="space-y-2">
                    {payments.map(p => (
                      <div key={p.odemeIslemId} className="border rounded p-2 text-sm">
                        <div className="flex justify-between">
                          <div className="font-medium">{trCurrency(p.tutarBrut, p.paraBirimiKodu)}</div>
                          <span className="text-xs text-gray-600">{fmtDate(p.olusturmaTarihi)}</span>
                        </div>
                        <div className="text-xs text-gray-600">
                          {p.odemeSaglayici || "—"} {p.islemAnahtari ? `• ${p.islemAnahtari}` : ""} {" • "}
                          <Badge kind="pay" code={p.islemDurumu} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Ödeme ekle */}
                <div className="mt-3 border-t pt-3">
                  <div className="text-sm font-medium mb-2">Ödeme / İade Ekle</div>
                  <div className="grid grid-cols-2 gap-2">
                    <input className="h-9 border rounded px-2 text-sm" placeholder="Sağlayıcı" value={pProvider} onChange={e => setPProvider(e.target.value)} />
                    <input className="h-9 border rounded px-2 text-sm" placeholder="Ref/İşlem No" value={pRef} onChange={e => setPRef(e.target.value)} />
                    <input className="h-9 border rounded px-2 text-sm" type="number" placeholder="Tutar" value={pAmount || ""} onChange={e => setPAmount(Number(e.target.value))} />
                    <select className="h-9 border rounded px-2 text-sm" value={pType} onChange={(e) => setPType(Number(e.target.value) as any)}>
                      <option value="1">Tahsil</option>
                      <option value="2">İade</option>
                      <option value="3">Kısmi İade</option>
                    </select>
                  </div>
                  <button className="mt-2 h-9 px-3 rounded text-white text-sm" style={{ background: "#16a34a" }} onClick={addPayment}><Plus size={16} className="inline mr-1" />Ekle</button>
                </div>
              </div>

              {/* Sevkiyatlar */}
              <div className="bg-white border rounded-lg shadow-sm p-3">
                <div className="flex items-center gap-2 mb-2"><Truck size={16} /><div className="font-semibold">Sevkiyatlar</div></div>
                {shipments.length === 0 ? (
                  <div className="text-sm text-gray-500">Kayıt yok.</div>
                ) : (
                  <div className="space-y-2">
                    {shipments.map(s => (
                      <ShipmentRow key={s.sevkiyatId} s={s} onUpdate={(payload) => updateShipment(s.sevkiyatId, payload)} onDelete={() => deleteShipment(s.sevkiyatId)} />
                    ))}
                  </div>
                )}
                {/* Ekle */}
                <div className="mt-3 border-t pt-3">
                  <div className="text-sm font-medium mb-2">Sevkiyat Ekle</div>
                  <div className="grid grid-cols-2 gap-2">
                    <input className="h-9 border rounded px-2 text-sm" placeholder="Kargo Şirketi" value={shCompany} onChange={e => setShCompany(e.target.value)} />
                    <input className="h-9 border rounded px-2 text-sm" placeholder="Takip No" value={shTrack} onChange={e => setShTrack(e.target.value)} />
                    <select className="h-9 border rounded px-2 text-sm" value={shStatus} onChange={e => setShStatus(Number(e.target.value))}>
                      <option value="0">Oluşturuldu</option>
                      <option value="1">Hazırlanıyor</option>
                      <option value="2">Yolda</option>
                      <option value="3">Dağıtımda</option>
                      <option value="4">Teslim Edilemedi</option>
                      <option value="7">Teslim</option>
                    </select>
                    <input className="h-9 border rounded px-2 text-sm" placeholder="Not" value={shNote} onChange={e => setShNote(e.target.value)} />
                  </div>
                  <button className="mt-2 h-9 px-3 rounded text-white text-sm" style={{ background: "#2563eb" }} onClick={addShipment}><Plus size={16} className="inline mr-1" />Ekle</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// Tek satır sevkiyat editable
const ShipmentRow: React.FC<{
  s: import("../../../api/adminOrders").MSevkiyatDto;
  onUpdate: (payload: Partial<{ kargoSirketAdi: string; takipNo: string; durumKodu: number; notlar: string; }>) => void;
  onDelete: () => void;
}> = ({ s, onUpdate, onDelete }) => {
  const [edit, setEdit] = useState(false);
  const [kargo, setKargo] = useState(s.kargoSirketAdi || "");
  const [takip, setTakip] = useState(s.takipNo || "");
  const [durum, setDurum] = useState<number>(s.durumKodu);
  const [not, setNot] = useState(s.notlar || "");

  const save = async () => { await onUpdate({ kargoSirketAdi: kargo, takipNo: takip, durumKodu: durum, notlar: not }); setEdit(false); };

  return (
    <div className="border rounded p-2 text-sm bg-white shadow-sm">
      {!edit ? (
        <div className="flex flex-col gap-1">
          <div className="flex justify-between">
            <div className="font-medium">{kargo || "—"} {takip ? `• ${takip}` : ""}</div>
            <div className="text-xs text-gray-600">{fmtDate(s.olusturmaTarihi)}</div>
          </div>
          <div className="text-xs text-gray-600">Durum: {durum}</div>
          {!!not && <div className="text-xs text-gray-600">Not: {not}</div>}
          <div className="flex gap-2 mt-1">
            <button className="h-8 px-2 border rounded" onClick={() => setEdit(true)}><Pencil size={14} /></button>
            <button className="h-8 px-2 border rounded text-rose-600" onClick={onDelete}><Trash2 size={14} /></button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <input className="h-8 border rounded px-2 text-sm" placeholder="Kargo" value={kargo} onChange={e => setKargo(e.target.value)} />
          <input className="h-8 border rounded px-2 text-sm" placeholder="Takip" value={takip} onChange={e => setTakip(e.target.value)} />
          <select className="h-8 border rounded px-2 text-sm" value={durum} onChange={e => setDurum(Number(e.target.value))}>
            <option value="0">Oluşturuldu</option>
            <option value="1">Hazırlanıyor</option>
            <option value="2">Yolda</option>
            <option value="3">Dağıtımda</option>
            <option value="4">Teslim Edilemedi</option>
            <option value="7">Teslim</option>
          </select>
          <input className="h-8 border rounded px-2 text-sm" placeholder="Not" value={not} onChange={e => setNot(e.target.value)} />
          <div className="col-span-2 flex gap-2">
            <button className="h-8 px-3 rounded text-white text-sm" style={{ background: "#16a34a" }} onClick={save}><Check size={14} /></button>
            <button
              className="h-8 px-3 border rounded text-sm"
              onClick={() => { setEdit(false); setKargo(s.kargoSirketAdi || ""); setTakip(s.takipNo || ""); setDurum(s.durumKodu); setNot(s.notlar || ""); }}
            ><X size={14} /></button>
          </div>
        </div>
      )}
    </div>
  );
};
